import { CREATURE_TYPES, type CreatureType } from "./creatures.ts";
import type { Difficulty, Mode } from "./match.ts";
import { hauntDamageMul, hauntHpMul } from "./night.ts";
import type { SimpatiaRarity } from "./simpatias.ts";

/** Os quatro chefes, na ordem do bestiário (doc 06). */
export const BOSS_TYPES: readonly CreatureType[] = CREATURE_TYPES.filter((t) => t.archetype === "chefe");
export const BOSS_IDS: readonly string[] = BOSS_TYPES.map((t) => t.id);

/**
 * Regras comuns aos chefes (doc 06, Chefes; doc 01, Noites de chefe e Chefes invocados). Hipóteses
 * de balanceamento, centralizadas aqui.
 */
export const BOSS = {
  /** Nenhum golpe tira mais que isto da vida máxima. */
  hitCap: 0.05,
  /** Quebras de postura (fração da vida) e o atordoamento de cada uma (s). */
  breaks: [0.75, 0.5, 0.25],
  breakStun: 2,
  /** Aviso (rugido e marcador) antes de surgir, e a clareira em volta do centro do grupo (m). */
  warning: 6,
  ring: [60, 80],
  /** Em noite de chefe, o orçamento das assombrações cai para esta fração. */
  hauntBudget: 0.5,
  /** Simpatia comum do chefe para quem deu o golpe final: 80 % Rara, 20 % Lendária. */
  simpatia: { comum: 0, rara: 0.8, lendaria: 0.2 } satisfies Record<SimpatiaRarity, number>,
  /** Chefe invocado na Pedra de Encanto: sempre reforçado. */
  summoned: { hp: 1.4, damage: 1.2 },
} as const;

/** Pedras de Encanto (doc 01 e doc 06): alcance, contagem, Desafio e a coleira do chefe invocado. */
export const ENCANTO = {
  reach: 4,
  countdown: 3,
  /** Desafio: 3 + ⌊n/2⌋ assombrações do dia + 2, vida ×1,6, arena de 18 m de raio; falha com o grupo fora por 15 s. */
  challenge: { base: 3, dayOffset: 2, hp: 1.6, arena: 18, outside: 15, spawn: [6, 10] },
  challengeSimpatia: { comum: 0.55, rara: 0.35, lendaria: 0.1 } satisfies Record<SimpatiaRarity, number>,
  /** Chefe invocado some (e a Pedra volta) se ninguém fica a até 40 m da Pedra por 15 s. */
  leash: 40,
  leashSeconds: 15,
} as const;

/** Calendário das noites de chefe (doc 01, tabela): primeira noite e intervalo, por dificuldade. */
export const BOSS_NIGHTS: Record<Difficulty, { first: number; every: number }> = {
  tranquilo: { first: 4, every: 4 },
  arretado: { first: 3, every: 3 },
  sinistro: { first: 3, every: 2 },
};

/** A noite depois do dia `day` é de chefe? Só no Mutirão (doc 01). */
export function isBossNight(mode: Mode, difficulty: Difficulty, day: number): boolean {
  if (mode !== "mutirao") return false;
  const { first, every } = BOSS_NIGHTS[difficulty];
  return day >= first && (day - first) % every === 0;
}

/** Fator de vida pelos jogadores vivos: 0,7 + 0,15·n (doc 06). */
export const bossPlayerMul = (players: number): number => 0.7 + 0.15 * Math.max(1, players);

/**
 * Vida e dano de um chefe: base do bestiário × curva do dia e da dificuldade × jogadores (vida);
 * o invocado na Pedra é reforçado (×1,4 vida, ×1,2 dano).
 */
export function bossMods(mode: Mode, difficulty: Difficulty, day: number, players: number, summoned: boolean): { hpMul: number; damageMul: number } {
  return {
    hpMul: hauntHpMul(mode, difficulty, day) * bossPlayerMul(players) * (summoned ? BOSS.summoned.hp : 1),
    damageMul: hauntDamageMul(mode, difficulty, day) * (summoned ? BOSS.summoned.damage : 1),
  };
}

/** Pedras-de-raio (fragmentos) que o chefe solta: 1 + ⌊n/3⌋ (doc 01). */
export const bossRaio = (players: number): number => 1 + Math.floor(Math.max(1, players) / 3);

/** Invocadas por vez: `count + ⌊n/2⌋·perTwoPlayers`. */
export const summonCount = (rules: { count: number; perTwoPlayers: number }, players: number): number =>
  rules.count + Math.floor(Math.max(1, players) / 2) * rules.perTwoPlayers;

/** Assombrações do Desafio: 3 + ⌊n/2⌋. */
export const challengeCount = (players: number): number => ENCANTO.challenge.base + Math.floor(Math.max(1, players) / 2);

/** Saco embaralhado (Fisher–Yates) com os quatro chefes; `rand` devolve [0, 1). */
export function shuffledBosses(rand: () => number): string[] {
  const bag = [...BOSS_IDS];
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [bag[i], bag[j]] = [bag[j] as string, bag[i] as string];
  }
  return bag;
}

/** Os cinco Guardiões dos Muiraquitãs, na ordem das cores (verde, azul, vermelho, amarelo, rosa). */
export const GUARDIAN_TYPES: readonly CreatureType[] = CREATURE_TYPES.filter((t) => t.archetype === "guardiao");
export const guardianOf = (color: string): CreatureType | undefined => GUARDIAN_TYPES.find((t) => t.id === `guardiao-${color}`);
/** Cor do santuário (`santuario-<cor>`) ou `undefined`. */
export const shrineColor = (itemId: string): string | undefined => (itemId.startsWith("santuario-") ? itemId.slice("santuario-".length) : undefined);

/** Guardiões (doc 06): escala do dia (mínimo do dia 2) × (0,8 + 0,1·n) na vida. */
export const GUARDIAN = { minDay: 2, reach: 4 } as const;
export const guardianPlayerMul = (players: number): number => 0.8 + 0.1 * Math.max(1, players);

export function guardianMods(mode: Mode, difficulty: Difficulty, day: number, players: number): { hpMul: number; damageMul: number } {
  const d = Math.max(GUARDIAN.minDay, day);
  return { hpMul: hauntHpMul(mode, difficulty, d) * guardianPlayerMul(players), damageMul: hauntDamageMul(mode, difficulty, d) };
}

/** Fragmentos de Pedra-de-raio do Guardião: 2 + 1 por jogador (doc 03, tabela de loot). */
export const guardianRaio = (players: number): number => 2 + Math.max(1, players);

/**
 * Nome dos golpes dos chefes na barra de conjuração (F63; nomes do doc de chefes, tema de fantasia).
 * O id é o interno; golpe sem nome aqui aparece pelo id, com espaços.
 */
export const ATTACK_NAMES: Readonly<Record<string, string>> = {
  pancada: "Pancada dupla",
  "pancada-investida": "Pancada com investida",
  investida: "Investida",
  tronco: "Pedregulho",
  bafo: "Rugido",
  investidas: "Três investidas",
  fogo: "Sopro de fogo",
  coice: "Coice",
  caldeiroes: "Chuva de poções",
  cantiga: "Canto fúnebre",
  raizes: "Mãos da cova",
  varrida: "Varrida",
  onda: "Onda",
  raios: "Raios",
  cuspe: "Cuspe de fogo",
  bote: "Bote",
  chicote: "Chicotada",
  sopro: "Sopro",
  rastro: "Rastro de fogo",
  chuva: "Chuva de brasas",
  salto: "Salto",
  rochedo: "Rochedo",
  marrada: "Marrada",
  lamento: "Lamento dos mortos",
};

/** Dica da barra de conjuração para as mecânicas de grupo (F69). */
export const MECHANIC_HINTS = { share: "juntem-se", buster: "no tanque", interruptible: "interrompa!" } as const;

export function attackName(id: string): string {
  const name = ATTACK_NAMES[id] ?? id.replace(/-/g, " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
}
